import { Dispatch, lazy, SetStateAction, Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { Plus } from "@phosphor-icons/react"
import { Button, ButtonVariant } from "@/components/ui/button"
import { toast } from "@/hooks/useToast"
import { UpdateWizardPatientFileLockProvider } from "@/modules/inbound-wizard/contexts/update-wizard-patient-file-lock-context"
import { useOpenPatientInboundCall } from "@/modules/patients/hooks/useOpenPatientInboundCall"
import useCallContextStore from "@/stores/useCallContextStore"
import { Country, Language } from "@/types/__generated__/union-types"

// Since this feature in-progress and not available for production now
// So, we just used lazy import to reduce load time for development mode and bundle size for production mode
const ChPatientForm = lazy(() =>
  import("@/modules/inbound-wizard/components/ch/create-ch-patient-form").then((m) => ({ default: m.ChPatientForm })),
)
const DePatientForm = lazy(() =>
  import("@/modules/inbound-wizard/components/de/create-de-patient-form").then((m) => ({ default: m.DePatientForm })),
)
const ChUpdatePatientFormWrapper = lazy(() =>
  import("@/modules/inbound-wizard/components/ch/update-ch-patient-form").then((m) => ({
    default: m.ChUpdatePatientFormWrapper,
  })),
)
const DeUpdatePatientFormWrapper = lazy(() =>
  import("@/modules/inbound-wizard/components/de/update-de-patient-form").then((m) => ({
    default: m.DeUpdatePatientFormWrapper,
  })),
)

export type TelephonyInformation = {
  callerPhoneNumber: string
  callLineCode?: string
  callId?: string | null
  getNotes: () => string
  inboundCallStartedAt: Date
  callLineLanguage?: Language
}

export const NewPatientWizard = ({
  variant,
  defaultCountry,
  patientQuery,
  setPatientQuery,
}: {
  variant?: ButtonVariant
  defaultCountry: Country
  patientQuery: string
  setPatientQuery?: (value: string) => void
}) => {
  const callerPhoneNumber = useCallContextStore((state) => state.callerPhoneNumber)
  const callLineCode = useCallContextStore((state) => state.callLineCode)
  const callId = useCallContextStore((state) => state.callID)
  const inboundCallStartedAt = useCallContextStore((state) => state.inboundCallStartedAt)
  const callLineLanguage = useCallContextStore((state) => state.callLineLanguage)

  return (
    <NewPatientWizardInternal
      variant={variant}
      defaultCountry={defaultCountry}
      patientQuery={patientQuery}
      setPatientQuery={setPatientQuery}
      telephonyInformation={{
        callerPhoneNumber,
        callLineCode,
        callId,
        getNotes: () => useCallContextStore.getState().temporaryNotes,
        inboundCallStartedAt,
        callLineLanguage,
      }}
    />
  )
}

const NewPatientWizardInternal = (props: {
  variant?: ButtonVariant
  defaultCountry: Country
  telephonyInformation: TelephonyInformation
  patientQuery: string
  setPatientQuery?: (value: string) => void
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const { telephonyInformation, setPatientQuery, defaultCountry, variant } = props

  const handleOpenChange = (open: boolean) => setOpen(open)

  const { openPatientInboundCall, patientInboundCallDialog } = useOpenPatientInboundCall()

  return (
    <>
      <Button variant={variant} onClick={() => setOpen(true)} data-testid="open-new-ibc-button">
        <Plus />
        {t("patient.header.patient")}
      </Button>
      <Suspense>
        {open ? (
          defaultCountry === "CH" ? (
            <ChPatientForm
              onOpenChange={handleOpenChange}
              patientQuery={props.patientQuery}
              setPatientQuery={setPatientQuery}
              telephonyInformation={telephonyInformation}
              defaultCountry={defaultCountry}
              openPatientInboundCall={openPatientInboundCall}
            />
          ) : (
            <DePatientForm
              onOpenChange={handleOpenChange}
              setPatientQuery={setPatientQuery}
              telephonyInformation={telephonyInformation}
              defaultCountry={defaultCountry}
              openPatientInboundCall={openPatientInboundCall}
            />
          )
        ) : null}
      </Suspense>
      {patientInboundCallDialog}
    </>
  )
}

export const UpdatePatientWizard = ({
  defaultCountry,
  patientId,
  patientName,
  open,
  onOpenChange,
  shouldHideCallReason,
  setPatientQuery,
}: {
  defaultCountry: Country
  patientId: string
  patientName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  shouldHideCallReason?: boolean
  setPatientQuery?: Dispatch<SetStateAction<string>>
}) => {
  const callerPhoneNumber = useCallContextStore((state) => state.callerPhoneNumber)
  const callId = useCallContextStore((state) => state.callID)
  const callLineCode = useCallContextStore((state) => state.callLineCode)
  const inboundCallStartedAt = useCallContextStore((state) => state.inboundCallStartedAt)
  const callLineLanguage = useCallContextStore((state) => state.callLineLanguage)

  return (
    <UpdatePatientWizardInternal
      defaultCountry={defaultCountry}
      patientId={patientId}
      patientName={patientName}
      open={open}
      onOpenChange={onOpenChange}
      setPatientQuery={setPatientQuery}
      shouldHideCallReason={!!shouldHideCallReason}
      telephonyInformation={{
        callerPhoneNumber,
        callLineCode,
        callId,
        getNotes: () => useCallContextStore.getState().temporaryNotes,
        inboundCallStartedAt,
        callLineLanguage,
      }}
    />
  )
}

type UpdatePatientFormProps = {
  defaultCountry: Country
  patientId: string
  telephonyInformation: TelephonyInformation
  setPatientQuery?: Dispatch<SetStateAction<string>>
  shouldHideCallReason: boolean
}

const UpdatePatientForm = ({
  defaultCountry,
  patientId,
  telephonyInformation,
  setPatientQuery,
  shouldHideCallReason,
}: UpdatePatientFormProps) =>
  defaultCountry === "CH" ? (
    <ChUpdatePatientFormWrapper
      patientId={patientId}
      setPatientQuery={setPatientQuery}
      telephonyInformation={telephonyInformation}
      shouldHideCallReason={shouldHideCallReason}
    />
  ) : (
    <DeUpdatePatientFormWrapper
      patientId={patientId}
      setPatientQuery={setPatientQuery}
      telephonyInformation={telephonyInformation}
      shouldHideCallReason={shouldHideCallReason}
    />
  )

const UpdatePatientWizardInternal = ({
  defaultCountry,
  patientId,
  patientName,
  open,
  telephonyInformation,
  onOpenChange,
  setPatientQuery,
  shouldHideCallReason,
}: {
  defaultCountry: Country
  patientId: string
  patientName: string
  open: boolean
  onOpenChange: (open: boolean) => void
  setPatientQuery?: Dispatch<SetStateAction<string>>
  telephonyInformation: TelephonyInformation
  shouldHideCallReason: boolean
}) => {
  const { t } = useTranslation()

  const ErrorFallback = () => {
    toast({ description: t("patient.toast.error") })
    return null
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      {open && (
        <Suspense>
          <UpdateWizardPatientFileLockProvider
            patientId={patientId}
            patientName={patientName}
            shouldHideCallReason={shouldHideCallReason}
            onOpenChange={onOpenChange}>
            <UpdatePatientForm
              defaultCountry={defaultCountry}
              patientId={patientId}
              telephonyInformation={telephonyInformation}
              setPatientQuery={setPatientQuery}
              shouldHideCallReason={shouldHideCallReason}
            />
          </UpdateWizardPatientFileLockProvider>
        </Suspense>
      )}
    </ErrorBoundary>
  )
}
